import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Trash2, XCircle, Archive, RefreshCw, Send } from "lucide-react";
import { cn } from "@/lib/utils";

type ConfirmationType = "delete" | "cancel" | "archive" | "reset" | "send" | "destructive" | "warning" | "info";

interface AffectedItem {
  label: string;
  value: string | number;
  highlight?: boolean;
}

interface SmartConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  type?: ConfirmationType;
  title: string;
  description?: string;
  affectedItems?: AffectedItem[];
  confirmText?: string;
  cancelText?: string;
  requireTyping?: string; // Si se proporciona, el usuario debe escribir esto para confirmar
  loading?: boolean;
}

const typeConfig: Record<ConfirmationType, { icon: React.ElementType; color: string; buttonClass: string }> = {
  delete: { icon: Trash2, color: "text-red-500", buttonClass: "bg-red-600 hover:bg-red-700" },
  cancel: { icon: XCircle, color: "text-orange-500", buttonClass: "bg-orange-600 hover:bg-orange-700" },
  archive: { icon: Archive, color: "text-gray-500", buttonClass: "bg-gray-600 hover:bg-gray-700" },
  reset: { icon: RefreshCw, color: "text-yellow-500", buttonClass: "bg-yellow-600 hover:bg-yellow-700" },
  send: { icon: Send, color: "text-blue-500", buttonClass: "bg-blue-600 hover:bg-blue-700" },
  destructive: { icon: AlertTriangle, color: "text-red-500", buttonClass: "bg-red-600 hover:bg-red-700" },
  warning: { icon: AlertTriangle, color: "text-yellow-500", buttonClass: "bg-yellow-600 hover:bg-yellow-700" },
  info: { icon: AlertTriangle, color: "text-blue-500", buttonClass: "bg-blue-600 hover:bg-blue-700" },
};

export function SmartConfirm({
  open,
  onOpenChange,
  onConfirm,
  type = "warning",
  title,
  description,
  affectedItems,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  requireTyping,
  loading = false,
}: SmartConfirmProps) {
  const [typedValue, setTypedValue] = React.useState("");
  const [isConfirming, setIsConfirming] = React.useState(false);

  const config = typeConfig[type];
  const Icon = config.icon;

  const canConfirm = requireTyping ? typedValue === requireTyping : true;

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      onOpenChange(false);
      setTypedValue("");
    } finally {
      setIsConfirming(false);
    }
  };

  React.useEffect(() => {
    if (!open) {
      setTypedValue("");
    }
  }, [open]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className={cn("p-2 rounded-full bg-muted", config.color)}>
              <Icon className="w-5 h-5" />
            </div>
            <AlertDialogTitle>{title}</AlertDialogTitle>
          </div>
          {description && (
            <AlertDialogDescription className="mt-2">
              {description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {/* Mostrar items afectados */}
        {affectedItems && affectedItems.length > 0 && (
          <div className="my-4 p-3 bg-muted/50 rounded-lg space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Elementos afectados
            </p>
            {affectedItems.map((item, idx) => (
              <div key={idx} className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">{item.label}</span>
                <span className={cn("font-medium", item.highlight && "text-primary")}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Campo de confirmación por escritura */}
        {requireTyping && (
          <div className="my-4 space-y-2">
            <Label className="text-sm">
              Escribe <Badge variant="secondary" className="mx-1 font-mono">{requireTyping}</Badge> para confirmar
            </Label>
            <Input
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder={requireTyping}
              className={cn(
                typedValue === requireTyping && "border-green-500 focus-visible:ring-green-500"
              )}
              autoFocus
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isConfirming || loading}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!canConfirm || isConfirming || loading}
            className={cn(config.buttonClass, "text-white")}
          >
            {isConfirming || loading ? "Procesando..." : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Hook para usar confirmaciones de forma imperativa
interface ConfirmOptions extends Omit<SmartConfirmProps, "open" | "onOpenChange" | "onConfirm"> {}

interface ConfirmState {
  open: boolean;
  options: ConfirmOptions | null;
  resolve: ((value: boolean) => void) | null;
}

export function useSmartConfirm() {
  const [state, setState] = React.useState<ConfirmState>({
    open: false,
    options: null,
    resolve: null,
  });

  const confirm = React.useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        open: true,
        options,
        resolve,
      });
    });
  }, []);

  const handleOpenChange = React.useCallback((open: boolean) => {
    if (!open && state.resolve) {
      state.resolve(false);
    }
    setState((prev) => ({ ...prev, open }));
  }, [state.resolve]);

  const handleConfirm = React.useCallback(() => {
    if (state.resolve) {
      state.resolve(true);
    }
    setState({ open: false, options: null, resolve: null });
  }, [state.resolve]);

  const ConfirmDialog = React.useCallback(() => {
    if (!state.options) return null;
    return (
      <SmartConfirm
        {...state.options}
        open={state.open}
        onOpenChange={handleOpenChange}
        onConfirm={handleConfirm}
      />
    );
  }, [state, handleOpenChange, handleConfirm]);

  return { confirm, ConfirmDialog };
}

// Funciones de conveniencia para casos comunes
export function useDeleteConfirm() {
  const { confirm, ConfirmDialog } = useSmartConfirm();

  const confirmDelete = React.useCallback(
    (itemName: string, itemId?: string | number) => {
      return confirm({
        type: "delete",
        title: "Eliminar elemento",
        description: `¿Estás seguro de que deseas eliminar "${itemName}"? Esta acción no se puede deshacer.`,
        affectedItems: itemId ? [{ label: "ID", value: itemId }] : undefined,
        confirmText: "Eliminar",
      });
    },
    [confirm]
  );

  return { confirmDelete, ConfirmDialog };
}

export function useCancelConfirm() {
  const { confirm, ConfirmDialog } = useSmartConfirm();

  const confirmCancel = React.useCallback(
    (itemName: string, additionalInfo?: AffectedItem[]) => {
      return confirm({
        type: "cancel",
        title: "Cancelar operación",
        description: `¿Estás seguro de que deseas cancelar "${itemName}"?`,
        affectedItems: additionalInfo,
        confirmText: "Sí, cancelar",
      });
    },
    [confirm]
  );

  return { confirmCancel, ConfirmDialog };
}
